import { setupAddEventListener, checkIfCompatibleWithBackend, } from "@todesktop/client-util";
import { invoke } from "../invoke.js";
/**
 * @public
 */
const _appEventMappings = {
    openProtocolURL: "open-protocol-url",
};
/**
 * An interface for manipulating and querying app-wide preferences.
 *
 * @public
 */
/**
 * The current version of this app.
 * @public
 */
export const getVersion = () => window["todesktop"].version;
/**
 * Create a new window.
 * The created window will automatically load the configured app URL.
 * @public
 */
export async function createNewWindow() {
    return window["todesktop"].app.createNewWindow();
}
/**
 * Get the app's current launch settings. Seee {@link LaunchSettings}.
 * @public
 */
export async function getLaunchSettings() {
    return window["todesktop"].app.getLaunchSettings();
}
/**
 * Sets the app's launch settings. See {@link LaunchSettings}.
 *
 * @param settings - Desired launch settings for this app.
 * @public
 */
export async function setLaunchSettings(settings) {
    return window["todesktop"].app.setLaunchSettings(settings);
}
/**
 * @returns A list of URL patterns that this app is currently handling.
 * @public
 */
export async function getInternalURLs() {
    return window["todesktop"].app.getInternalUrls();
}
/**
 * Update what URLs are routed to this app. URLs that are not listed will be routed to the default browser.
 * This will over-ride any previously set internal URLs.
 *
 *
 * @param urlRegex - A regular expression string representing URLs for this app to handle. Example: '.*(google\.com).*'.
 * @public
 */
export async function setInternalURLs(urlRegex) {
    return window["todesktop"].app.updateInternalUrls(urlRegex);
}
/**
 * Hide all windows associated with the current app.
 * @public
 */
export async function hide() {
    return window["todesktop"].app.hide();
}
/**
 * Show all windows associated with the current app.
 * @public
 */
export async function show() {
    return window["todesktop"].app.show();
}
/**
 * Show all windows associated with the current app and set focus on the
 * last opened window.
 * @public
 */
export async function focus(options) {
    return window["todesktop"].app.focus(options);
}
/**
 * Sets a numeric badge on the app icon in the user's task bar/dock.
 *
 * @param count - An integer specifying the badge to set. A value of `0` will
 * remove the badge.
 * @public
 */
export async function setBadgeCount(count) {
    return window["todesktop"].app.setBadgeCount(count);
}
/**
 * @returns The current number set as the app's badge.
 * @public
 */
export async function getBadgeCount() {
    return window["todesktop"].app.getBadgeCount();
}
/**
 * Get the capture sources available on the user's machine.
 * @public
 */
export async function getCaptureSources(options) {
    return window["todesktop"].desktopCapturer.getSources(options);
}
/**
 * Try to close all windows and quit the app.
 *
 * This method guarantees that all `beforeunload` and `unload` event handlers are
 * correctly executed. It is possible that a window cancels the quitting by
 * returning `false` in the `beforeunload` event handler.
 * @public
 */
export async function quit() {
    return invoke({
        namespace: "app",
        method: "quit",
        args: [],
    });
}
/**
 * @public
 */
export const on = setupAddEventListener(_appEventMappings);
/**
 * Executes a given function just once for the entire application.
 *
 * @param id - Unique identifier for the target function to be executed.
 * @param callback - Target function to be executed.
 * @returns The value that the target function returns.
 * @public
 */
export async function once(id, callback) {
    if (checkIfCompatibleWithBackend("app:once", "2.6.0")) {
        return window["todesktop"].app.once(id, callback);
    }
}
